import React, { useState, useRef, useEffect } from "react";
import type { RowRef } from "@src/types/timeline";
import { useQueryClient } from "@tanstack/react-query";
import { firebase_auth } from "@src/utility_functions/database_operations/config/firebaseConfig";
import { TextInput } from "@src/components/Forms/FormElements/Inputs/TextInput";
import { DurationInputsNew } from "@src/components/Forms/FormElements/Inputs/DurationInputsNew";
import { ColorInputs } from "@src/components/Forms/FormElements/Inputs/ColorInputs";
import { TextAreaInput } from "@src/components/Forms/FormElements/Inputs/TextAreaInput";
import { validateInput } from "@src/utility_functions/inputValidation/validateInput";
import { createInterval } from "@src/utility_functions/database_operations/intervals/createInterval";
import {
  validateLabel,
  validateDescription,
  validateStartTime,
  validateEndTime,
  validatePrimaryColor,
  validateSecondaryColor,
} from "#shared/inputValidation/inputValidation.js";
import { generate_random_color } from "#shared/utility_functions/color_functions.js";
import {
  addMinutesToDate,
  formatDateForInputElement,
} from "@src/utility_functions/utility_functions/date_functions";
import { labelInputPlaceholderContent } from "@src/components/Static/input_placeholder_content";
import { getRandomWholeNumber } from "@src/utility_functions/utility_functions/object_functions";
import { EmojiPicker } from "@src/components/Forms/FormElements/Inputs/EmojiPicker/EmojiPicker";
import { RowSelectInput } from "@src/components/Forms/FormElements/Inputs/RowSelectInput";
import {
  CHART_TYPE_TIMELINE,
  CHART_TYPE_ROW,
} from "#shared/constants/chartTypes.js";
import { useModal } from "@src/utility_functions/customHooks/useModal";

// No-verdict-yet state for validation results, matching the shared
// validators' {errorClass, errorMessages} shape (same idiom as
// UpdateIntervalForm) — these previously initialized to bare "".
const emptyValidationState = { errorClass: null, errorMessages: [] };

type CreateIntervalFormProps = {
  customClass?: string;
  formID?: string;
  chartType: "row" | "timeline";
  chartID: string;
  // RowRef[] is what every producer actually passes (TimelineChartRow.tsx:313
  // passed it against the old RowObjectType[] declaration as a BASELINE
  // TS2322; RowPage/Router construct {chartID, ownerID, rowNumber} literals)
  // and all RowSelectInput reads (ownerID, chartID) are RowRef fields —
  // audited in the 2.4 coupling sweep.
  rowsIncludedState: RowRef[];
  defaultStartTime?: Date;
  ownerID?: string;
};

export const CreateIntervalForm = (props: CreateIntervalFormProps) => {
  const { closeDialog } = useModal();
  const ownerID = firebase_auth?.currentUser.uid;
  const intervalLabelInputRef = useRef<HTMLInputElement>(null);
  const intervalStartTimeInputRef = useRef<HTMLInputElement>(null);
  const intervalEndTimeInputRef = useRef<HTMLInputElement>(null);
  const [intervalLabelError, setIntervalLabelError] =
    useState(emptyValidationState);
  const [intervalDescriptionError, setIntervalDescriptionError] =
    useState(emptyValidationState);
  const [intervalStartTimeError, setIntervalStartTimeError] =
    useState(emptyValidationState);
  const [intervalEndTimeError, setIntervalEndTimeError] =
    useState(emptyValidationState);
  const defaultPrimaryColor = generate_random_color("color");
  const defaultSecondaryColor = generate_random_color("shade");
  const [primaryColorValue, setPrimaryColorValue] =
    useState(defaultPrimaryColor);
  const [secondaryColorValue, setSecondaryColorValue] = useState(
    defaultSecondaryColor,
  );
  const [intervalPrimaryColorError, setIntervalPrimaryColorError] =
    useState(emptyValidationState);
  const [intervalSecondaryColorError, setIntervalSecondaryColorError] =
    useState(emptyValidationState);
  const [selectedEmoji, setSelectedEmoji] = useState<string | null>(null);
  const todayAtNoon = new Date();
  todayAtNoon.setHours(12, 0, 0, 0);
  const defaultStartTime = formatDateForInputElement(
    props.defaultStartTime || todayAtNoon,
  );
  const defaultEndTime = formatDateForInputElement(
    addMinutesToDate(props.defaultStartTime || todayAtNoon, 30),
  );
  const [startTimeValue, setStartTimeValue] = useState(defaultStartTime);
  const [endTimeValue, setEndTimeValue] = useState(defaultEndTime);
  const [selectedRow, setSelectedRow] = useState(null);
  const queryClient = useQueryClient();

  // Sync start/end values when the provided defaultStartTime changes (e.g., new Cmd+Click)
  useEffect(() => {
    const baseStart: Date = props.defaultStartTime
      ? new Date(props.defaultStartTime)
      : new Date(todayAtNoon);
    const newStart = formatDateForInputElement(baseStart);
    const newEnd = formatDateForInputElement(addMinutesToDate(baseStart, 30));
    setStartTimeValue(newStart);
    setEndTimeValue(newEnd);
  }, [props.defaultStartTime]);

  const submitHandler = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    const formData = new FormData(event.currentTarget);
    const formDataEntries = Object.fromEntries(formData.entries());
    const labelValid =
      validateLabel(formDataEntries.label).errorClass === "valid";
    const descriptionValid =
      validateDescription(formDataEntries.description).errorClass === "valid";
    const primaryColorValid =
      validatePrimaryColor(primaryColorValue).errorClass === "valid";
    const secondaryColorValid =
      validateSecondaryColor(secondaryColorValue).errorClass === "valid";
    const startTimeValid =
      validateStartTime([startTimeValue, endTimeValue]).errorClass === "valid";
    const endTimeValid =
      validateEndTime([startTimeValue, endTimeValue]).errorClass === "valid";

    const formValid =
      labelValid &&
      descriptionValid &&
      primaryColorValid &&
      secondaryColorValid &&
      startTimeValid &&
      endTimeValid;
    const selectedChartID =
      props.chartType === CHART_TYPE_ROW
        ? props.chartID
        : selectedRow || formDataEntries.chartID;
    if (formValid) {
      const intervalData = {
        label: formDataEntries.label,
        description: formDataEntries.description,
        startTime: startTimeValue,
        endTime: endTimeValue,
        primaryColor: primaryColorValue,
        secondaryColor: secondaryColorValue,
        emoji: selectedEmoji,
        chartID: selectedChartID,
        source: "createIntervalFormWeb",
      };
      const serverResponse = await createInterval(intervalData);
      if (!serverResponse) {
        console.error("Failed to create interval");
        return;
      }
      queryClient.invalidateQueries({
        queryKey: ["rowIntervals", ownerID, selectedChartID],
      });
      closeDialog();
      if (intervalLabelInputRef.current) {
        intervalLabelInputRef.current.value = "";
      }
      if (intervalStartTimeInputRef.current) {
        intervalStartTimeInputRef.current.value = defaultStartTime;
      }
      if (intervalEndTimeInputRef.current) {
        intervalEndTimeInputRef.current.value = defaultEndTime;
      }
      setStartTimeValue(defaultStartTime);
      setEndTimeValue(defaultEndTime);
      setPrimaryColorValue(defaultPrimaryColor);
      setSecondaryColorValue(defaultSecondaryColor);
      setIntervalLabelError(emptyValidationState);
      setIntervalDescriptionError(emptyValidationState);
      setIntervalStartTimeError(emptyValidationState);
      setIntervalEndTimeError(emptyValidationState);
    } else {
      validateInput(
        formDataEntries.label,
        validateLabel,
        setIntervalLabelError,
      );
      validateInput(
        formDataEntries.description,
        validateDescription,
        setIntervalDescriptionError,
      );
      validateInput(
        [startTimeValue, endTimeValue],
        validateStartTime,
        setIntervalStartTimeError,
      );
      validateInput(
        [startTimeValue, endTimeValue],
        validateEndTime,
        setIntervalEndTimeError,
      );
      validateInput(
        primaryColorValue,
        validatePrimaryColor,
        setIntervalPrimaryColorError,
      );
      validateInput(
        secondaryColorValue,
        validateSecondaryColor,
        setIntervalSecondaryColorError,
      );
    }
  };

  return (
    <div className={props.customClass}>
      <form noValidate={true} onSubmit={submitHandler} id={props.formID}>
        <TextInput
          ref={intervalLabelInputRef}
          inputId="addIntervalLabelInput"
          name="label"
          inputType="text"
          labelText="Label"
          maxlength={50}
          defaultValue=""
          placeholder={
            labelInputPlaceholderContent[
              getRandomWholeNumber(0, labelInputPlaceholderContent.length - 1)
            ]
          }
          autocomplete=""
          required={true}
          validationResponse={intervalLabelError}
          onChangeFunction={(event: React.FocusEvent<HTMLInputElement>) =>
            validateInput(
              event.target.value,
              validateLabel,
              setIntervalLabelError,
            )
          }
        />
        <DurationInputsNew
          intervalStartTimeInputRef={intervalStartTimeInputRef}
          defaultStartTime={defaultStartTime}
          startTimeValue={startTimeValue}
          setStartTimeValue={setStartTimeValue}
          intervalStartTimeError={intervalStartTimeError}
          setIntervalStartTimeError={setIntervalStartTimeError}
          intervalEndTimeInputRef={intervalEndTimeInputRef}
          defaultEndTime={defaultEndTime}
          endTimeValue={endTimeValue}
          setEndTimeValue={setEndTimeValue}
          intervalEndTimeError={intervalEndTimeError}
          setIntervalEndTimeError={setIntervalEndTimeError}
        />
        {props.chartType === CHART_TYPE_TIMELINE && (
          <RowSelectInput
            rowsIncludedState={props.rowsIncludedState}
            setSelectedRow={setSelectedRow}
            loggedInUserID={ownerID}
          />
        )}

        <div className="_margin-bottom--1rem">
          <p>Optional</p>
          <hr />
        </div>
        <ColorInputs
          primaryColorValue={primaryColorValue}
          setPrimaryColorValue={setPrimaryColorValue}
          primaryColorError={intervalPrimaryColorError}
          secondaryColorValue={secondaryColorValue}
          setSecondaryColorValue={setSecondaryColorValue}
          secondaryColorError={intervalSecondaryColorError}
        />
        <EmojiPicker
          className="_margin-bottom--1rem"
          selectedEmoji={selectedEmoji}
          setSelectedEmoji={setSelectedEmoji}
        />
        <TextAreaInput
          inputId="addIntervalDescriptionInput"
          inputType="description"
          labelText="Description"
          maxlength={1000}
          defaultValue=""
          placeholder="Description is optional"
          required={false}
          validationResponse={intervalDescriptionError}
          onChangeFunction={(event: React.FocusEvent<HTMLInputElement>) =>
            validateInput(
              event.target.value,
              validateDescription,
              setIntervalDescriptionError,
            )
          }
        />
      </form>
    </div>
  );
};
